import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import PageTransition from '@/components/PageTransition';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import FloatingLabel from '@/components/FloatingLabel';
import MovingBanner from '@/components/MovingBanner';
import { Brain, Moon, Smile, Beaker, Mail } from 'lucide-react';
import { useUserData } from '@/context/UserDataContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { getResultsByEmail } from '@/integrations/supabase/savedResults';
import { usePageTitle } from '@/hooks/usePageTitle';

const Index = () => {
  const navigate = useNavigate();
  const { setUserData } = useUserData();
  const [isVisible, setIsVisible] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  usePageTitle('Personalized Supplement Recommendations');

  useEffect(() => {
    // Start animations after a short delay to ensure page transition is complete
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 400);
    return () => clearTimeout(timer);
  }, []);

  const handleTestClick = () => {
    // Set mock data for testing
    setUserData({
      age: 30,
      gender: 'male',
      goals: [
        { value: 'mental_clarity', priority: 1 },
        { value: 'energy', priority: 2 },
        { value: 'fitness', priority: 3 }
      ],
      currentStep: 'complete',
      isBasicFormOnly: false,
      dietaryRestrictions: ['vegetarian'],
      currentSupplements: [],
      healthConcerns: [],
      allergies: [],
      exerciseFrequency: 'four_plus_times_a_week',
      exerciseIntensity: 'moderate',
      exerciseType: ['strength', 'cardio_endurance'],
      sleepQuality: 'good',
      stressLevel: 'moderate',
      alcoholConsumption: 'once_a_week',
      smokingStatus: 'non_smoker',
      smoking: false,
      waterIntake: 'adequate',
      meatConsumption: 'never',
      seafoodConsumption: 'never',
      dairyConsumption: 'once_a_week',
      lastQuestion: 'smoking'
    });
    navigate('/results');
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    if (newEmail && !validateEmail(newEmail)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
  };

  const handleEmailSubmit = async () => {
    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    setIsSaving(true);
    try {
      const result = await getResultsByEmail(email);

      if (result.success && result.data) {
        // Set the user data in context
        setUserData(prev => ({
          ...prev,
          ...result.data.userData,
          isBasicFormOnly: false
        }));

        // Store recommendations in localStorage for the ResultsFullPage to use
        localStorage.setItem('recommendations', JSON.stringify(result.data.recommendations));

        // Extract just the hardlink ID from the full URL
        const hardlinkId = result.data.hardlink.split('/').pop() || '';
        
        // Navigate to the results page
        navigate(`/results/${hardlinkId}`);
      } else {
        // Show a more specific error message
        toast.error('No saved results found', {
          description: 'Oops! It looks like we don\'t have any results saved under this email address.'
        });
      }
    } catch (error) {
      console.error('Error retrieving results:', error);
      toast.error('Unable to retrieve results', {
        description: 'There was a problem accessing your saved recommendations. Please try again.'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.25
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 35 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        ease: "easeOut"
      }
    }
  };

  return (
    <PageTransition>
      <motion.div 
        className="relative min-h-screen w-screen bg-gradient-to-b from-background via-background/95 to-background overflow-hidden grid grid-rows-[1fr_auto]"
        initial="hidden"
        animate={isVisible ? "visible" : "hidden"}
        variants={containerVariants}
      >
        {/* Background patterns */}
        <motion.div 
          className="absolute inset-0 bg-[radial-gradient(circle_500px_at_50%_200px,var(--primary-color-alpha),transparent)]"
          variants={itemVariants}
        />
        <motion.div 
          className="absolute inset-0" 
          style={{
            backgroundImage: 'radial-gradient(rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }}
          variants={itemVariants}
        />

        {/* Main content */}
        <motion.div 
          className="relative z-10 container mx-auto px-4 flex flex-col"
          variants={itemVariants}
        >
          {/* Upper content section */}
          <div className="flex-1 flex flex-col items-center pt-[5vh] lg:pt-[10vh]">
            {/* Badges */}
            <motion.div 
              className="flex flex-wrap justify-center gap-2 lg:gap-4 mb-4 lg:mb-8 px-4"
              variants={itemVariants}
            >
              <Badge variant="secondary" className="bg-white/10 border border-amber-900/40 text-amber-900/90 text-[0.7rem] lg:text-[0.8rem] px-3 lg:px-4">
                AI-Driven Personalization
              </Badge>
              <Badge variant="secondary" className="bg-white/10 border border-amber-900/40 text-amber-900/90 text-[0.7rem] lg:text-[0.8rem] px-3 lg:px-4">
                Zero Bias
              </Badge>
              <Badge variant="secondary" className="bg-white/10 border border-amber-900/40 text-amber-900/90 text-[0.7rem] lg:text-[0.8rem] px-3 lg:px-4">
                Clinically Accurate Data
              </Badge>
            </motion.div>

            {/* Text content */}
            <motion.div 
              variants={itemVariants}
              className="text-center max-w-5xl px-4"
            >
              <h1 className="text-6xl sm:text-6xl md:text-8xl font-bold mb-12 mt-4 lg:mt-0 lg:mb-10 font-['Merriweather']">
                Personalized<br />
                Supplement Analysis
              </h1>
              <p className="text-[1rem] lg:text-xl text-muted-foreground mb-8 lg:mb-12 mx-auto text-balance">
                Become a healthier, better version of yourself with unbiased, research based recommendations tailored to your <em>exact</em> needs.
              </p>
              
              {/* CTA Buttons */}
              <motion.div
                variants={itemVariants}
                className="mt-16 lg:mt-4 flex flex-col sm:flex-row gap-4 justify-center items-center max-w-[80%] mx-auto"
              >
                <div className="flex flex-col items-center gap-2 w-full sm:w-auto px-4 sm:px-0">
                  <Button 
                    size="lg"
                    className="h-12 lg:h-14 px-6 lg:px-8 text-base lg:text-lg rounded-full w-full sm:w-auto"
                    onClick={() => navigate('/questionnaire')}
                  >
                    Start Your Analysis
                  </Button>
                  <button 
                    onClick={() => setShowEmailModal(true)}
                    className="text-xs lg:text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 mt-2 lg:mt-4"
                  >
                    Already have saved recommendations? Access them here.
                  </button>
                </div>
                
                {/* Button for testing results. Can re-enable if needed.
                <Button 
                  size="lg"
                  variant="outline"
                  className="h-14 px-8 text-lg rounded-full flex items-center gap-2"
                  onClick={handleTestClick}
                >
                  <Beaker className="h-5 w-5" />
                  Test Results
                </Button> */}
              </motion.div>
            </motion.div>
          </div>
        </motion.div>

        {/* Image and floating labels - Grid-based positioning */}
        <motion.div 
          variants={itemVariants}
          className="relative h-[600px] lg:h-[800px] -mb-[300px] lg:-mb-[500px]"
        >
          <div className="absolute inset-0 flex items-end justify-center">
            <img 
              src={`${import.meta.env.BASE_URL}assets/images/hand_holding_pill.png`}
              alt="Hand holding a pill"
              className="w-[400px] lg:w-[700px] ml-[-200px] lg:ml-[-325px]"
            />
          </div>
        </motion.div>

        {/* Moving Banner */}
        <motion.div variants={itemVariants} className="mt-8 lg:mt-0">
          <MovingBanner />
        </motion.div>
      </motion.div>

      {/* Email Collection Modal */}
      <Dialog 
        open={showEmailModal} 
        onOpenChange={setShowEmailModal}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className='mb-4'>Retrieve Your Results</DialogTitle>
            <DialogDescription>
              Enter your email address to load your last saved recommendations.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={(e) => {
            e.preventDefault();
            if (email && validateEmail(email)) {
              handleEmailSubmit();
            }
          }}>
            <div className="py-4">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={handleEmailChange}
                disabled={isSaving}
              />
            </div>
            <div className="flex justify-center gap-2">
              <Button 
                variant="outline" 
                type="button" 
                onClick={() => setShowEmailModal(false)}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <TooltipProvider delayDuration={100}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button 
                        type="submit"
                        disabled={!email || !validateEmail(email) || isSaving}
                      >
                        {isSaving ? (
                          <>
                            <span className="mr-2">Checking</span>
                            <svg
                              className="animate-spin h-4 w-4"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              />
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              />
                            </svg>
                          </>
                        ) : (
                          'Load Recommendations'
                        )}
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {(!email || !validateEmail(email)) && !isSaving && (
                    <TooltipContent>
                      <p>Please enter a valid email address to continue</p>
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </PageTransition>
  );
};

export default Index;
